import React, { useState } from 'react';
import { Modal } from './Modal';

type ImportResult = { success: boolean; error?: string };

type Props = {
  open: boolean;
  onImport: (json: string) => ImportResult;
  onClose: () => void;
};

export const ImportJsonDialog: React.FC<Props> = ({ open, onImport, onClose }) => {
  const [text, setText] = useState('');
  const [error, setError] = useState('');

  const handleImport = () => {
    const result = onImport(text.trim());
    if (result.success) {
      setText('');
      setError('');
      onClose();
    } else {
      setError(result.error ?? 'Unknown error');
    }
  };

  const handleClose = () => {
    setText('');
    setError('');
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title="Import from JSON" width={520}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 13, color: '#666' }}>
          Paste a previously exported Gantt JSON.
          <br />
          Expected format: <code style={{ fontSize: 12 }}>{'{ tasks, rows, dependencies }'}</code>
        </div>

        <textarea
          value={text}
          onChange={e => { setText(e.target.value); setError(''); }}
          placeholder={'{\n  "tasks": [],\n  "rows": [],\n  "dependencies": []\n}'}
          rows={14}
          autoFocus
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '8px',
            border: `1px solid ${error ? '#dc2626' : '#d0d0d0'}`,
            borderRadius: 4,
            fontSize: 12,
            fontFamily: 'monospace',
            resize: 'vertical',
            outline: 'none',
          }}
        />

        {error && (
          <div style={{ fontSize: 12, color: '#dc2626', background: '#fef2f2', padding: '6px 8px', borderRadius: 4 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={handleClose} style={cancelBtnStyle}>Cancel</button>
          <button
            onClick={handleImport}
            disabled={text.trim().length === 0}
            style={{ ...importBtnStyle, opacity: text.trim().length === 0 ? 0.5 : 1 }}
          >
            Import
          </button>
        </div>
      </div>
    </Modal>
  );
};

const cancelBtnStyle: React.CSSProperties = {
  padding: '6px 14px',
  background: '#f5f5f5',
  color: '#333',
  border: '1px solid #d0d0d0',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 13,
};

const importBtnStyle: React.CSSProperties = {
  padding: '6px 14px',
  background: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 13,
};
